"use client"

import Link from "next/link"
import { ArrowRight, Package } from "lucide-react"
import type { ProductRecommendation } from "@/data/guide-product-recommendations"
import { contentAnalytics } from "@/lib/analytics"
import { withAmazonTag } from "@/lib/affiliate"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import { railHostFromSourcePrefix } from "@/components/guides/guide-sidebar-product"

interface GuideSidebarLedgerProps {
  slug: string
  products: ProductRecommendation[]
  /** Unified-telemetry surface for the impression and every row click. */
  surface: string
  /** Experiment arm stamped on the impression and clicks. */
  railVariant: string
  sourcePrefix?: string
}

/**
 * The `inline` arm of guide_affiliate_rail_v1 ("Direction A, quiet ledger").
 * Same glass card and header as the compact rail, but each pick is a ranked
 * ledger row: serif numeral, name, one-line why, price, and a "Check price"
 * text link instead of a button. One impression per rail (`denominator:
 * 'container'`), the same counting as the control rail, so the two arms share
 * a denominator.
 */
export function GuideSidebarLedger({
  slug,
  products,
  surface,
  railVariant,
  sourcePrefix = "guide-sidebar",
}: GuideSidebarLedgerProps) {
  const railHost = railHostFromSourcePrefix(sourcePrefix)

  const impressionRef = useAffiliateImpression(surface, {
    article_slug: slug,
    placement: "sidebar",
    product_count: products.length,
    denominator: "container",
    rail_host: railHost,
    guide_rail_variant: railVariant,
  })

  if (products.length === 0) return null

  const handleClick = (product: ProductRecommendation, href: string, position: number) => {
    contentAnalytics.affiliateLinkClicked(slug, product.name, href, "sidebar", undefined, { position })
    contentAnalytics.affiliateProductClicked(surface, {
      productName: product.name,
      affiliateLink: href,
      position,
      article_slug: slug,
      placement: "sidebar",
      rail_host: railHost,
      guide_rail_variant: railVariant,
    })
  }

  return (
    <div
      ref={impressionRef}
      className="ds-glass rounded-[18px] p-4"
      style={{ boxShadow: "0 4px 16px -8px rgba(18,46,30,0.22), 0 1px 3px -1px rgba(18,46,30,0.12)" }}
    >
      <div className="mb-3 flex items-center gap-2">
        <span className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-lg border border-white/70 bg-green-50/85 text-green-700">
          <Package className="h-3.5 w-3.5" />
        </span>
        <h3 className="text-[11px] font-bold uppercase tracking-[0.08em] text-green-700">Top Picks</h3>
      </div>

      <ol className="m-0 list-none p-0">
        {products.map((product, index) => (
          <li key={product.name} className="border-t border-gray-200 first:border-t-0">
            <a
              href={withAmazonTag(product.affiliateUrl, "rail")}
              target="_blank"
              rel="noopener noreferrer sponsored nofollow"
              data-affiliate="true"
              data-affiliate-placement="sidebar"
              onClick={() => handleClick(product, withAmazonTag(product.affiliateUrl, "rail"), index + 1)}
              className="group flex gap-2.5 py-3"
            >
              <span
                aria-hidden="true"
                className="w-[18px] flex-shrink-0 font-editorial text-[22px] font-medium leading-none text-green-200 group-hover:text-green-400"
              >
                {index + 1}
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-[5px]">
                <span className="text-[13px] font-semibold leading-[1.3] text-gray-900 group-hover:text-green-700">
                  {product.name}
                </span>
                <span className="text-[11.5px] leading-[1.45] text-gray-500">{product.description}</span>
                <span className="mt-0.5 flex items-center justify-between">
                  <span className="text-[12px] font-semibold text-gray-900">{product.price}</span>
                  <span className="inline-flex items-center gap-1 text-[11.5px] font-bold text-green-700 group-hover:text-green-800">
                    Check price
                    <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </span>
              </span>
            </a>
          </li>
        ))}
      </ol>

      <div className="mt-3 border-t border-white/45 pt-3">
        <Link
          href={`/products?source=${encodeURIComponent(`${sourcePrefix}-${slug}`)}`}
          className="group inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:text-green-800"
        >
          See all products
          <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>
    </div>
  )
}
